import { useEffect, useMemo, useRef } from 'react';

interface UsePaginatedItemSearchParams<T> {
  allPages?: { data?: T[] }[];
  isEnabled: boolean;
  hasNextPage?: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  maxLookaheadPages?: number;
  findItem: (records: T[]) => T | undefined;
}

interface UsePaginatedItemSearchResult<T> {
  item?: T;
  allRecords: T[];
  searchExhausted: boolean;
  isSearching: boolean;
}

const MAX_LOOKAHEAD_PAGES = 2;

export function usePaginatedItemSearch<T>({
  allPages,
  isEnabled,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
  maxLookaheadPages = MAX_LOOKAHEAD_PAGES,
  findItem,
}: UsePaginatedItemSearchParams<T>): UsePaginatedItemSearchResult<T> {
  const lookaheadAttempts = useRef(0);

  const allRecords = useMemo(() => (allPages ?? []).flatMap((page) => page?.data ?? []), [allPages]);

  const item = useMemo(() => findItem(allRecords), [allRecords, findItem]);

  const wantsLookahead = isEnabled && !item;

  const searchExhausted = !isFetching && (!hasNextPage || lookaheadAttempts.current >= maxLookaheadPages);

  const isSearching = isEnabled && (isFetching || isFetchingNextPage);

  useEffect(() => {
    if (!wantsLookahead || isFetchingNextPage) return;
    if (!hasNextPage || lookaheadAttempts.current >= maxLookaheadPages) {
      return;
    }
    lookaheadAttempts.current += 1;
    fetchNextPage();
  }, [wantsLookahead, hasNextPage, isFetchingNextPage, fetchNextPage, maxLookaheadPages]);

  useEffect(() => {
    if (!isEnabled) {
      lookaheadAttempts.current = 0;
    }
  }, [isEnabled]);

  return {
    item,
    allRecords,
    searchExhausted,
    isSearching,
  };
}
